<script setup lang="ts">
import { computed, ref } from 'vue'

interface RankingItem {
  rank: number
  name: string
  sum: number
  margeCompteurSubscription?: number
  margeCompteurOneShot?: number
  regulSubscription?: number
  regulOneShot?: number
}

interface Props {
  title?: string
  valueLabel?: string
  items: RankingItem[]
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Classement',
  valueLabel: 'TOTAL',
})

type SortKey = 'total' | 'recurrent' | 'oneShot'

const sortKey = ref<SortKey>('total')
const sortDir = ref<'asc' | 'desc'>('desc')

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

const hasBreakdown = (item: RankingItem) =>
  item.margeCompteurSubscription != null ||
  item.margeCompteurOneShot != null ||
  item.regulSubscription != null ||
  item.regulOneShot != null

const recurrentTotal = (item: RankingItem) =>
  (item.margeCompteurSubscription ?? 0) + (item.regulSubscription ?? 0)

const oneShotTotal = (item: RankingItem) =>
  (item.margeCompteurOneShot ?? 0) + (item.regulOneShot ?? 0)

const sortValue = (item: RankingItem, key: SortKey): number => {
  switch (key) {
    case 'recurrent':
      return recurrentTotal(item)
    case 'oneShot':
      return oneShotTotal(item)
    default:
      return item.sum
  }
}

const setSort = (key: SortKey) => {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'desc' ? 'asc' : 'desc'
  } else {
    sortKey.value = key
    sortDir.value = 'desc'
  }
}

const sortedItems = computed(() => {
  const list = [...props.items]
  const dir = sortDir.value === 'desc' ? -1 : 1
  const key = sortKey.value
  list.sort((a, b) => dir * (sortValue(a, key) - sortValue(b, key)))
  return list.map((item, i) => ({ ...item, rank: i + 1 }))
})
</script>

<template>
  <div class="card bg-base-100 shadow-md h-full flex flex-col min-w-0">
    <div class="p-4 border-b border-base-200">
      <h2 class="text-lg text-center">{{ title }}</h2>
    </div>
    <div class="overflow-x-auto overflow-y-auto grow custom-scrollbar max-h-72">
      <table class="table table-pin-rows ranking-table w-full">
        <thead>
          <tr class="border-none">
            <th class="ranking-th w-14 text-center">RANG</th>
            <th class="ranking-th min-w-24 text-left">NOM</th>
            <th class="ranking-th text-right border-l border-base-200">
              <button
                type="button"
                class="sort-th"
                :class="{ 'sort-th--active': sortKey === 'recurrent' }"
                @click="setSort('recurrent')"
              >
                <span>Réc.</span>
                <span v-if="sortKey === 'recurrent'" class="sort-th__indicator" aria-hidden="true">{{
                  sortDir === 'desc' ? '↓' : '↑'
                }}</span>
              </button>
            </th>
            <th class="ranking-th text-right">
              <button
                type="button"
                class="sort-th"
                :class="{ 'sort-th--active': sortKey === 'oneShot' }"
                @click="setSort('oneShot')"
              >
                <span>OneShot</span>
                <span v-if="sortKey === 'oneShot'" class="sort-th__indicator" aria-hidden="true">{{
                  sortDir === 'desc' ? '↓' : '↑'
                }}</span>
              </button>
            </th>
            <th class="ranking-th w-26 text-right border-l border-base-200">
              <button
                type="button"
                class="sort-th"
                :class="{ 'sort-th--active': sortKey === 'total' }"
                @click="setSort('total')"
              >
                <span>{{ valueLabel }}</span>
                <span v-if="sortKey === 'total'" class="sort-th__indicator" aria-hidden="true">{{
                  sortDir === 'desc' ? '↓' : '↑'
                }}</span>
              </button>
            </th>
          </tr>
        </thead>
        <tbody class="text-slate-600">
          <tr v-for="item in sortedItems" :key="`${item.name}-${item.rank}`" class="hover:bg-slate-50 border-none transition-colors">
            <td class="text-center align-middle py-2">
              <div class="flex justify-center items-center">
                <template v-if="item.rank === 1">
                  <svg width="28" height="28" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 6L16 16L21 6" stroke="#4F46E5" stroke-width="3" stroke-linecap="round" />
                    <circle cx="16" cy="18" r="8" fill="#FBBF24" stroke="#D97706" stroke-width="1" />
                    <text x="16" y="21" text-anchor="middle" fill="#D97706" font-size="9" font-weight="bold" font-family="sans-serif">1</text>
                  </svg>
                </template>
                <template v-else-if="item.rank === 2">
                  <svg width="28" height="28" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 6L16 16L21 6" stroke="#818CF8" stroke-width="3" stroke-linecap="round" />
                    <circle cx="16" cy="18" r="8" fill="#E2E8F0" stroke="#94A3B8" stroke-width="1" />
                    <text x="16" y="21" text-anchor="middle" fill="#94A3B8" font-size="9" font-weight="bold" font-family="sans-serif">2</text>
                  </svg>
                </template>
                <template v-else-if="item.rank === 3">
                  <svg width="28" height="28" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 6L16 16L21 6" stroke="#A5B4FC" stroke-width="3" stroke-linecap="round" />
                    <circle cx="16" cy="18" r="8" fill="#FDBA74" stroke="#C2410C" stroke-width="1" />
                    <text x="16" y="21" text-anchor="middle" fill="#C2410C" font-size="9" font-weight="bold" font-family="sans-serif">3</text>
                  </svg>
                </template>
                <template v-else>
                  <span class="text-slate-400 font-medium text-sm">{{ item.rank }}</span>
                </template>
              </div>
            </td>
            <td class="align-middle py-2">
              <span class="font-bold text-slate-700 uppercase text-xs tracking-tight">{{ item.name }}</span>
            </td>
            <td class="text-right align-middle text-xs tabular-nums text-slate-700 border-l border-base-100 py-2">
              <template v-if="hasBreakdown(item)">{{ formatCurrency(recurrentTotal(item)) }}</template>
              <span v-else class="text-slate-300">—</span>
            </td>
            <td class="text-right align-middle text-xs tabular-nums text-slate-700 py-2">
              <template v-if="hasBreakdown(item)">{{ formatCurrency(oneShotTotal(item)) }}</template>
              <span v-else class="text-slate-300">—</span>
            </td>
            <td class="text-right align-middle font-semibold text-slate-700 text-sm tabular-nums border-l border-base-100 py-2 pr-3">
              {{ formatCurrency(item.sum) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.ranking-table.table th {
  border: none;
  background-color: #f1f5f9;
  position: sticky;
  top: 0;
  z-index: 10;
}

.ranking-th {
  padding: 10px 8px;
  vertical-align: middle;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
  line-height: 1.25;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #cbd5e1;
}

.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 transparent;
}

.sort-th {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  width: 100%;
  margin: 0;
  padding: 0;
  font: inherit;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 4px;
  line-height: 1.25;
  min-height: 1.25em;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.sort-th:hover {
  background-color: rgba(255, 255, 255, 0.6);
  color: #1e293b;
}

.sort-th--active {
  color: #1e293b;
}

.sort-th__indicator {
  font-size: 12px;
  line-height: 1;
  opacity: 0.85;
}
</style>
